import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { HardHat, CaretDown, List } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

const navLinkClass = ({ isActive }) =>
  cn(
    "px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
    isActive ? "bg-orange-50 text-[#FF7A00] font-semibold" : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50"
  );

const navLinkDrawerClass = ({ isActive }) =>
  cn(
    "flex w-full items-center rounded-lg px-4 py-3.5 text-base font-medium transition-colors",
    isActive ? "bg-orange-50 text-[#FF7A00] font-semibold" : "text-neutral-800 hover:bg-neutral-50"
  );

function NavbarAuthControls({ className, dataTestId = "nav-login-menu-trigger" }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const loginRouteActive =
    pathname.startsWith("/login") ||
    pathname.startsWith("/account") ||
    pathname.startsWith("/staff/login") ||
    pathname.startsWith("/supplier/login");

  if (user?.role === "buyer") {
    return (
      <div className={cn("flex items-center gap-2 shrink-0", className)}>
        <Button
          type="button"
          variant="outline"
          data-testid="nav-buyer-account-btn"
          className="rounded-full border-neutral-200 text-sm font-semibold h-10 px-4"
          onClick={() => navigate("/account")}
        >
          My account
        </Button>
        <Button
          type="button"
          variant="ghost"
          data-testid="nav-buyer-logout-btn"
          className="rounded-full text-sm font-medium text-neutral-600 h-10 px-3"
          onClick={() => {
            logout();
            navigate("/");
          }}
        >
          Sign out
        </Button>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="default"
          data-testid={dataTestId}
          aria-haspopup="menu"
          className={cn(
            "rounded-full bg-[#FF7A00] hover:bg-[#e86800] text-white text-sm font-semibold px-4 sm:px-5 h-10 shadow-sm border-0 gap-1 shrink-0",
            loginRouteActive && "ring-2 ring-orange-200 ring-offset-2 ring-offset-white",
            className
          )}
        >
          Login
          <CaretDown size={16} weight="bold" className="opacity-90 shrink-0" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]" data-testid="nav-login-menu-content">
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/account/login" data-testid="nav-login-buyer">
            Buyer
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/supplier/login" data-testid="nav-login-supplier">
            Supplier
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Shared public header: Browse suppliers, Browse blog, optional Post requirement,
 * and Login dropdown (Buyer / Supplier).
 */
export default function BuyerPublicNavbar({
  testId = "buyer-navbar",
  brandSuffix = null,
  showPostRequirement = false,
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const navLinks = (
    <>
      <NavLink to="/suppliers" data-testid="nav-browse-suppliers-btn" className={navLinkClass}>
        Browse suppliers
      </NavLink>
      <NavLink to="/old-material" data-testid="nav-old-material-btn" className={navLinkClass}>
        Old material
      </NavLink>
      <NavLink to="/about-us" data-testid="nav-about-us-btn" className={navLinkClass}>
        About us
      </NavLink>
      <NavLink to="/contact-us" data-testid="nav-contact-us-btn" className={navLinkClass}>
        Contact us
      </NavLink>
      <NavLink to="/blog" data-testid="nav-browse-blog-btn" className={navLinkClass}>
        Browse blog
      </NavLink>
      {showPostRequirement ? (
        <NavLink
          to="/"
          end
          data-testid="nav-post-requirement-btn"
          className={({ isActive }) => cn(navLinkClass({ isActive }), "hidden sm:inline-flex")}
        >
          Post requirement
        </NavLink>
      ) : null}
    </>
  );

  return (
    <>
      <nav
        className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
        data-testid={testId}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 h-14 sm:h-16">
            <div className="flex items-center gap-3 min-w-0 shrink">
              <Link to="/" className="flex items-center gap-3 min-w-0 shrink-0 hover:opacity-90 transition-opacity">
                <HardHat size={28} weight="bold" className="text-[#FF7A00] shrink-0" />
                <span className="text-lg sm:text-xl font-bold tracking-tight text-neutral-900 truncate">
                  Scaffoindia
                </span>
              </Link>
              {brandSuffix}
            </div>

            <div className="hidden sm:flex flex-nowrap items-center justify-end gap-1 min-w-0 flex-1">
              {navLinks}
              <div className="shrink-0 ml-2">
                <NavbarAuthControls dataTestId="nav-login-menu-trigger" />
              </div>
            </div>

            <div className="flex sm:hidden shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-12 w-12 min-h-[48px] min-w-[48px] rounded-xl text-[#FF7A00] hover:bg-orange-50 hover:text-[#e86800]"
                data-testid="nav-mobile-menu-trigger"
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="buyer-public-mobile-nav"
                onClick={() => setMobileMenuOpen(true)}
              >
                <List size={40} weight="bold" aria-hidden className="shrink-0" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent
          side="right"
          id="buyer-public-mobile-nav"
          className="flex w-[min(100vw-1rem,20rem)] flex-col gap-0 border-l border-neutral-200 p-0 sm:max-w-sm"
        >
          <SheetHeader className="border-b border-neutral-100 px-6 py-4 text-left">
            <SheetTitle className="text-base font-semibold text-neutral-900">Menu</SheetTitle>
          </SheetHeader>
          <div className="flex flex-1 flex-col overflow-y-auto px-3 py-4">
            <nav className="flex flex-col gap-1" aria-label="Primary">
              <SheetClose asChild>
                <NavLink
                  to="/suppliers"
                  data-testid="nav-browse-suppliers-drawer"
                  className={navLinkDrawerClass}
                >
                  Browse suppliers
                </NavLink>
              </SheetClose>
              <SheetClose asChild>
                <NavLink to="/old-material" data-testid="nav-old-material-drawer" className={navLinkDrawerClass}>
                  Old material
                </NavLink>
              </SheetClose>
              <SheetClose asChild>
                <NavLink
                  to="/about-us"
                  data-testid="nav-about-us-drawer"
                  className={navLinkDrawerClass}
                >
                  About us
                </NavLink>
              </SheetClose>
              <SheetClose asChild>
                <NavLink
                  to="/contact-us"
                  data-testid="nav-contact-us-drawer"
                  className={navLinkDrawerClass}
                >
                  Contact us
                </NavLink>
              </SheetClose>
              <SheetClose asChild>
                <NavLink
                  to="/blog"
                  data-testid="nav-browse-blog-drawer"
                  className={navLinkDrawerClass}
                >
                  Browse blog
                </NavLink>
              </SheetClose>
              {showPostRequirement ? (
                <SheetClose asChild>
                  <NavLink
                    to="/"
                    end
                    data-testid="nav-post-requirement-drawer"
                    className={navLinkDrawerClass}
                  >
                    Post requirement
                  </NavLink>
                </SheetClose>
              ) : null}
            </nav>

            <div className="mt-6 border-t border-neutral-200 pt-5">
              <p className="px-4 text-[11px] font-mono uppercase tracking-wide text-neutral-500">
                {user?.role === "buyer" ? "Account" : "Login"}
              </p>
              <div className="mt-2 flex flex-col gap-1">
                {user?.role === "buyer" ? (
                  <>
                    <SheetClose asChild>
                      <Link
                        to="/account"
                        data-testid="nav-buyer-account-drawer"
                        className="rounded-lg px-4 py-3 text-base font-medium text-neutral-800 hover:bg-neutral-50"
                      >
                        My account
                      </Link>
                    </SheetClose>
                    <button
                      type="button"
                      data-testid="nav-buyer-logout-drawer"
                      className="rounded-lg px-4 py-3 text-base font-medium text-red-700 hover:bg-red-50 text-left w-full"
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                        navigate("/");
                      }}
                    >
                      Sign out
                    </button>
                  </>
                ) : (
                  <SheetClose asChild>
                    <Link
                      to="/account/login"
                      data-testid="nav-login-buyer-drawer"
                      className="rounded-lg px-4 py-3 text-base font-medium text-neutral-800 hover:bg-neutral-50"
                    >
                      Buyer
                    </Link>
                  </SheetClose>
                )}
                <SheetClose asChild>
                  <Link
                    to="/supplier/login"
                    data-testid="nav-login-supplier-drawer"
                    className="rounded-lg px-4 py-3 text-base font-medium text-neutral-800 hover:bg-neutral-50"
                  >
                    Supplier
                  </Link>
                </SheetClose>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
